import { type SiteWithState, type Artifact } from "../api";
import {
  MKCard,
  MKCardHeader,
  MKCardTitle,
  MKCheckbox,
} from "@mytraai/mytrakit";

interface Props {
  site: SiteWithState;
  selectedKeys: Set<string>;
  onToggle: (selectionKey: string) => void;
}

// Represents a single row in the status table
interface StatusRow {
  appName: string;
  botId: number | null; // null for site-level rows
  botName: string | null; // null for site-level rows
  currentArtifact?: Artifact;
  pendingArtifact?: Artifact;
}

// Represents a scan error row
interface ScanErrorRow {
  name: string; // "Site" or bot name
  scanAt: string | null;
  error: string;
}

// Generate a unique selection key for a row
function makeSelectionKey(artifactId: number, botId: number | null): string {
  return `${artifactId}:${botId ?? "site"}`;
}

// Collect scan errors from site and bots
function getScanErrorRows(site: SiteWithState): ScanErrorRow[] {
  const rows: ScanErrorRow[] = [];

  if (site.scan_error) {
    rows.push({
      name: "Site",
      scanAt: site.scan_at,
      error: site.scan_error,
    });
  }

  for (const bot of site.bots || []) {
    if (bot.scan_error) {
      rows.push({
        name: bot.name,
        scanAt: bot.scan_at,
        error: bot.scan_error,
      });
    }
  }

  return rows;
}

// Format timestamp for display
function formatTimestamp(timestamp: string | null): string {
  if (!timestamp) return "Never";
  const date = new Date(timestamp);
  return date.toLocaleString();
}

// Build all rows: site-level rows + bot-level rows
function buildStatusRows(site: SiteWithState): StatusRow[] {
  const rows: StatusRow[] = [];
  const allApps = new Set<string>();

  // Collect all app names
  Object.keys(site.current_artifacts || {}).forEach((app) => allApps.add(app));
  Object.keys(site.pending_artifact || {}).forEach((app) => allApps.add(app));
  (site.bots || []).forEach((bot) => {
    Object.keys(bot.current_artifacts || {}).forEach((app) => allApps.add(app));
    Object.keys(bot.pending_artifact || {}).forEach((app) => allApps.add(app));
  });

  const sortedApps = Array.from(allApps).sort();

  // Add site-level rows for each app
  for (const appName of sortedApps) {
    const currentArtifact = site.current_artifacts?.[appName];
    const pendingArtifact = site.pending_artifact?.[appName];

    // Only add site-level row if there's data for it
    if (currentArtifact || pendingArtifact) {
      rows.push({
        appName,
        botId: null,
        botName: null,
        currentArtifact,
        pendingArtifact,
      });
    }
  }

  // Add bot-level rows
  for (const bot of site.bots || []) {
    for (const appName of sortedApps) {
      const currentArtifact = bot.current_artifacts?.[appName];
      const pendingArtifact = bot.pending_artifact?.[appName];

      // Only add bot row if there's data for it
      if (currentArtifact || pendingArtifact) {
        rows.push({
          appName,
          botId: bot.bot_id,
          botName: bot.name,
          currentArtifact,
          pendingArtifact,
        });
      }
    }
  }

  return rows;
}

export default function ArtifactList({ site, selectedKeys, onToggle }: Props) {
  const rows = buildStatusRows(site);
  const hasBots = (site.bots || []).length > 0;
  const scanErrorRows = getScanErrorRows(site);

  return (
    <div className="flex flex-col gap-4">
      {scanErrorRows.length > 0 && (
        <MKCard>
          <MKCardHeader className="border-b">
            <MKCardTitle className="text-content-destructive">
              Scan Errors
            </MKCardTitle>
          </MKCardHeader>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-tertiary text-left text-sm text-content-tertiary">
                  <th className="p-3">Name</th>
                  <th className="p-3">Last Scan</th>
                  <th className="p-3">Error</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-tertiary">
                {scanErrorRows.map((row) => (
                  <tr key={row.name}>
                    <td className="p-3 font-medium text-content-primary">
                      {row.name}
                    </td>
                    <td className="p-3 text-content-secondary">
                      {formatTimestamp(row.scanAt)}
                    </td>
                    <td className="p-3 text-content-destructive">
                      {row.error}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </MKCard>
      )}

      <MKCard>
        <MKCardHeader className="border-b">
          <MKCardTitle>Status</MKCardTitle>
        </MKCardHeader>

        {rows.length === 0 ? (
          <div className="p-4 text-content-tertiary text-center">
            No apps deployed
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-surface-tertiary text-left text-sm text-content-tertiary">
                  <th className="p-3 w-8"></th>
                  <th className="p-3">App</th>
                  {hasBots && <th className="p-3">Bot</th>}
                  <th className="p-3">Current</th>
                  <th className="p-3">Pending</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-tertiary">
                {rows.map((row) => {
                  const rowKey = `${row.appName}-${row.botName || "site"}`;
                  const selectionKey = row.pendingArtifact
                    ? makeSelectionKey(
                        row.pendingArtifact.artifact_id,
                        row.botId,
                      )
                    : null;
                  return (
                    <StatusRowComponent
                      key={rowKey}
                      row={row}
                      showBotColumn={hasBots}
                      selected={
                        selectionKey ? selectedKeys.has(selectionKey) : false
                      }
                      onToggle={
                        selectionKey ? () => onToggle(selectionKey) : undefined
                      }
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </MKCard>
    </div>
  );
}

interface StatusRowComponentProps {
  row: StatusRow;
  showBotColumn: boolean;
  selected: boolean;
  onToggle?: () => void;
}

function StatusRowComponent({
  row,
  showBotColumn,
  selected,
  onToggle,
}: StatusRowComponentProps) {
  const hasPending = !!row.pendingArtifact;

  return (
    <tr
      className={`transition-colors ${
        selected
          ? "bg-surface-brand/10"
          : hasPending
            ? "hover:bg-surface-secondary cursor-pointer"
            : ""
      }`}
      onClick={onToggle}
    >
      <td className="p-3">
        {hasPending && (
          <MKCheckbox
            checked={selected}
            onCheckedChange={onToggle}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </td>
      <td className="p-3 font-medium text-content-primary">{row.appName}</td>
      {showBotColumn && (
        <td className="p-3 text-content-secondary">{row.botName || ""}</td>
      )}
      <td className="p-3">
        {row.currentArtifact ? (
          <code className="text-sm bg-surface-tertiary px-1.5 py-0.5 rounded">
            {row.currentArtifact.branch} {row.currentArtifact.version}
          </code>
        ) : (
          <span className="text-content-tertiary">-</span>
        )}
      </td>
      <td className="p-3">
        <PendingCell artifact={row.pendingArtifact} />
      </td>
    </tr>
  );
}

function PendingCell({ artifact }: { artifact?: Artifact }) {
  if (!artifact) return null;

  return (
    <div className="flex items-center gap-2">
      <code className="text-sm bg-surface-brand/20 text-content-brand px-1.5 py-0.5 rounded">
        {artifact.branch} {artifact.version}
      </code>
      {artifact.pr_url && (
        <a
          href={artifact.pr_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-content-brand hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          PR
        </a>
      )}
      {artifact.ci_url && (
        <a
          href={artifact.ci_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-xs text-content-brand hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          CI
        </a>
      )}
    </div>
  );
}
